import type { ClassValue } from 'clsx';
import { range } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VSelect, type VSelectProps, cn } from '@/package';

type ItemCountSelectProps = {
  className?: ClassValue;
  itemType?: 'limit' | 'ea';
  itemValueRange?: {
    end: number;
    start: number;
    step?: number;
  };
} & Pick<VSelectProps, 'value' | 'onChange'>;

const LABEL_TRANSLATION_KEY_BY_ITEM_TYPE: {
  [K in Required<ItemCountSelectProps>['itemType']]: keyof TranslationMap;
} = {
  limit: 'common.showItemNum.creatine',
  ea: 'common.showEaItemNum.semimade',
};

export const ItemCountSelect = ({
  className,
  itemType = 'limit',
  itemValueRange = {
    start: 10,
    end: 20,
    step: 10,
  },
  ...rest
}: ItemCountSelectProps) => {
  const { t } = useTranslation();
  const itemList: VSelectProps['itemList'] = useMemo(() => {
    const { start, end, step = 1 } = itemValueRange;

    return range(start, end + (step ?? 1), step ?? 1).map(value => ({
      value,
      label: t(LABEL_TRANSLATION_KEY_BY_ITEM_TYPE[itemType], {
        itemNum: value,
      }),
    }));
  }, [itemType, itemValueRange, t]);

  return (
    <div className={cn(className)}>
      {itemList.length && (
        <VSelect
          {...rest}
          defaultValue={itemList[0].value}
          fluid
          itemList={itemList}
        />
      )}
    </div>
  );
};

ItemCountSelect.displayName = 'ItemCountSelect';
